import React, { useState, useEffect, useContext } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { isEmpty } from 'lodash';
import { useIntl } from 'react-intl';

import Form from '~components/forms/form';
import { UserContext } from '~contexts/user';
import { MAX_NOTE_LENGTH, MIN_NOTE_LENGTH } from '~src/constants';

import { InputControlled } from '~components/forms/input';
import { TextareaControlled } from '~components/forms/textarea';
import Button from '~components/button';
import CharacterCount from '~components/forms/character-count';

import './style.scss';

const PersonalDataForm = ({ onSubmit = () => {} }) => {
  const intl = useIntl();

  const { user, updateWorker, updateCustomer } = useContext(UserContext);
  const { accountType, firstName, operations, wish } = user;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const personalNote = accountType === 'worker' ? operations?.personalNote : wish?.personalNote;

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required().max(20).noEmailInContent().noPhoneInContent(),
    personalNote: Yup.string()
      .required()
      .min(MIN_NOTE_LENGTH)
      .max(MAX_NOTE_LENGTH)
      .noEmailInContent()
      .noPhoneInContent()
      .noUrlInContent(),
  });

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      firstName: firstName || '',
      personalNote: personalNote || '',
    },
  });

  const {
    getValues,
    errors,
    formState: { dirtyFields },
  } = methods;

  const watchNote = methods.watch('personalNote');

  useEffect(async () => {
    if (!isSubmitting) {
      return;
    }

    const formData = getValues();

    try {
      if (dirtyFields.firstName || dirtyFields.personalNote) {
        const newUser = { ...user, firstName: formData.firstName };
        if (accountType === 'client') {
          newUser.wish.personalNote = formData.personalNote;
          await updateCustomer(newUser);
        } else {
          newUser.operations.personalNote = formData.personalNote;
          await updateWorker(newUser);
        }
      }
      onSubmit();
    } catch (err) {
      console.log('Error while editing personal data', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting]);

  const handleFormSubmit = () => {
    if (isEmpty(errors) || !isSubmitting) {
      setIsSubmitting(true);
    }
  };

  return (
    <Form methods={methods} validationSchema={validationSchema} onSubmit={handleFormSubmit}>
      {/* FIRSTNAME */}
      <div className="c-fieldset">
        <div className="c-fieldset__label">
          {intl.formatMessage({
            id: 'containers.account.personal-data.form.fields.first-name.label',
            defaultMessage: 'First name',
          })}
        </div>
        <div className="c-fieldset__value">
          <InputControlled
            type="text"
            name="firstName"
            placeholder={intl.formatMessage({
              id: 'containers.account.personal-data.form.fields.first-name.placeholder',
              defaultMessage: 'First name',
            })}
            data-cy="c-first-name__input"
          />
        </div>
      </div>
      {/* PERSONAL NOTE */}
      <div className="c-fieldset">
        <div className="c-fieldset__header">
          <div className="c-fieldset__label">
            {intl.formatMessage({
              id: 'containers.account.personal-data.form.fields.personal-note.label',
              defaultMessage: 'About you',
            })}
          </div>
          <CharacterCount field={watchNote} max={MAX_NOTE_LENGTH} className="c-fieldset__info" />
        </div>
        <div className="c-fieldset__value">
          <TextareaControlled
            name="personalNote"
            className="c-personal-data__note-textarea"
            placeholder={intl.formatMessage({
              id: 'containers.account.personal-data.form.fields.personal-note.placeholder',
              defaultMessage: 'Tell us and future clients something about yourself',
            })}
            isAutoExpand
          />
        </div>
      </div>

      <div className="c-fieldset">
        <div className="c-modal__buttons">
          <Button
            name="submit"
            type="submit"
            className="c-btn"
            disabled={!isEmpty(methods.errors) || isSubmitting}
            isLoading={isSubmitting}
          >
            {intl.formatMessage({
              id: 'containers.account.personal-data.form.save',
              defaultMessage: 'Save',
            })}
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default PersonalDataForm;
